import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users } from 'lucide-react';
import DriverCard from './Drivers/DriverCard';
import AddDriverModal from './Drivers/AddDriverModal';
import API_URL from '../config';
import { readUserFromStorage, getSubscriptionTier } from '../utils/subscription';
import { getDriverLimit, getDriverLimitMessage } from '../config/pricingPublic';
import '../App.css';

function isAtDriverLimit(tier, count) {
  const max = getDriverLimit(tier);
  return count >= max;
}

const Drivers = () => {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [deletingDriver, setDeletingDriver] = useState(null);
  const [subscriptionTier, setSubscriptionTier] = useState('free');

  const refreshTier = useCallback(async () => {
    const local = readUserFromStorage();
    let tier = getSubscriptionTier(local);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/api/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data) {
        const merged = { ...local, ...data };
        tier = getSubscriptionTier(merged);
      }
    } catch {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${API_URL}/api/subscriptions/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (data?.tier) {
          tier = String(data.tier).toLowerCase().trim();
        }
      } catch {
        /* keep local tier */
      }
    }
    setSubscriptionTier(tier);
  }, []);

  useEffect(() => {
    refreshTier();
  }, [refreshTier]);

  const fetchDrivers = async () => {
    try {
      setIsLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/drivers`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setDrivers(response.data);
    } catch (err) {
      console.error('Error fetching drivers:', err);
      setError('Failed to load drivers. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, []);

  const handleEdit = (driver) => {
    setEditingDriver(driver);
    setShowAddModal(true);
  };

  const handleDelete = (driver) => {
    setDeletingDriver(driver);
  };

  const handleConfirmDelete = async () => {
    if (!deletingDriver) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/drivers/${deletingDriver.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setDeletingDriver(null);
      await fetchDrivers();
      await refreshTier();
    } catch (err) {
      console.error('Error deleting driver:', err);
      alert('Failed to delete driver. Please try again.');
    }
  };

  const handleModalSuccess = () => {
    fetchDrivers();
    refreshTier();
    setEditingDriver(null);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingDriver(null);
  };

  const atLimit = isAtDriverLimit(subscriptionTier, drivers.length);
  const limitHint = getDriverLimitMessage(subscriptionTier);
  const driverLimit = getDriverLimit(subscriptionTier);

  const addDriverButton = (
    <div className="drivers-add-driver-wrap">
      <button
        type="button"
        className={atLimit ? 'btn btn-secondary drivers-add-btn--disabled' : 'btn btn-primary'}
        disabled={atLimit}
        title={atLimit ? limitHint : undefined}
        onClick={() => {
          if (!atLimit) setShowAddModal(true);
        }}
      >
        + Add Driver
      </button>
      {atLimit && <p className="drivers-limit-hint">{limitHint}</p>}
    </div>
  );

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <p>Loading drivers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h1 className="page-title">Drivers</h1>
            <p className="page-subtitle">Manage your drivers and track compliance status</p>
            {driverLimit > 0 && (
              <p className="drivers-usage-subtitle">
                Drivers: {drivers.length} of {driverLimit} used
              </p>
            )}
          </div>
          {addDriverButton}
        </div>
      </div>

      {atLimit && (
        <div className="drivers-plan-banner">
          <p>{limitHint}</p>
          <button type="button" className="btn btn-secondary drivers-plan-banner-cta" onClick={() => navigate('/pricing')}>
            View plans
          </button>
        </div>
      )}

      {error && (
        <div className="dashboard-error">
          <p>{error}</p>
        </div>
      )}

      {drivers.length === 0 ? (
        <div className="empty-state-enhanced">
          <div className="empty-icon">
            <Users size={48} />
          </div>
          <h3>No drivers yet</h3>
          <p>
            {driverLimit === 0
              ? 'Upgrade your plan to add drivers and track DQ compliance.'
              : 'Add a driver you employ to start tracking their DQ compliance.'}
          </p>
          {!atLimit && driverLimit > 0 && (
            <div className="empty-cta">
              <button type="button" className="btn btn-primary" onClick={() => setShowAddModal(true)}>
                Add Driver
              </button>
            </div>
          )}
          {atLimit && (
            <div className="empty-cta">
              <button type="button" className="btn btn-secondary" onClick={() => navigate('/pricing')}>
                Upgrade plan
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="vehicles-grid">
          {drivers.map((driver) => (
            <DriverCard
              key={driver.id}
              driver={driver}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      <AddDriverModal
        isOpen={showAddModal}
        onClose={handleCloseModal}
        onSuccess={handleModalSuccess}
        driver={editingDriver}
      />

      {deletingDriver && (
        <div className="modal-overlay" onClick={() => setDeletingDriver(null)}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="delete-modal-title">Delete Driver</h3>
            <p className="delete-modal-text">
              Are you sure you want to delete <strong>{deletingDriver.name}</strong>? This action cannot be undone.
            </p>
            <div className="delete-modal-actions">
              <button type="button" className="modal-button cancel" onClick={() => setDeletingDriver(null)}>
                Cancel
              </button>
              <button type="button" className="modal-button delete" onClick={handleConfirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Drivers;
